import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  ArrowLeft, Plus, Loader2, Trash2, Pencil, Store, Star, Users, MapPin,
} from 'lucide-react';
import { useConfirm } from '../components/ConfirmDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUB_CATEGORIES = [
  { key: 'event-staffing', label: 'Event Staffing (full crew)' },
  { key: 'servers', label: 'Servers' },
  { key: 'bartenders', label: 'Bartenders' },
  { key: 'coordinators', label: 'Coordinators' },
  { key: 'security', label: 'Security' },
  { key: 'valet', label: 'Valet' },
  { key: 'setup-crew', label: 'Setup Crew' },
  { key: 'ushers', label: 'Ushers' },
  { key: 'brand-ambassadors', label: 'Brand Ambassadors' },
];

const PRICING_MODELS = [
  { key: 'hourly', label: 'Hourly rate' },
  { key: 'flat_rate', label: 'Flat rate per event' },
  { key: 'per_person', label: 'Per person' },
  { key: 'custom', label: 'Custom / request a quote' },
];

export default function MyStaffServices() {
  const { token, isAuthenticated } = useAuth();
  const confirm = useConfirm();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/staff-services/mine`, auth);
      setListings(res.data.listings || []);
    } catch (e) {
      toast.error('Failed to load listings');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { if (isAuthenticated) refresh(); }, [isAuthenticated, refresh]);

  const handleDelete = async (listing_id) => {
    const ok = await confirm({
      title: 'Delete this listing?',
      description: 'This removes it from Eventism Staffing, the Event Planning Hub, and the Supplier Hub.',
      confirmLabel: 'Delete',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/staff-services/${listing_id}`, auth);
      toast.success('Listing deleted');
      refresh();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const toggleActive = async (listing) => {
    try {
      await axios.patch(`${API}/staff-services/${listing.listing_id}`, { is_active: !listing.is_active }, auth);
      toast.success(listing.is_active ? 'Listing paused' : 'Listing activated');
      refresh();
    } catch (e) { toast.error('Failed to update'); }
  };

  if (!isAuthenticated) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center space-y-3">
        <p className="text-muted-foreground">Please sign in to manage your staffing services.</p>
        <Link to="/login?returnTo=/staffing/my-services"><Button>Sign in</Button></Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background pt-20 pb-12" data-testid="my-staff-services-page">
      <div className="max-w-6xl mx-auto px-4 space-y-6">
        <Link to="/staffing" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to Staffing
        </Link>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold font-['Space_Grotesk'] flex items-center gap-3">
              <Store className="h-7 w-7 text-indigo-400" />
              My Staffing Services
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Publish a service listing so clients can hire you directly — appears on Eventism Staffing, Supplier Hub, and the Event Planning Hub.
            </p>
          </div>
          <Button onClick={() => setEditing({})} className="bg-indigo-500 hover:bg-indigo-600" data-testid="new-listing-btn">
            <Plus className="h-4 w-4 mr-1" /> New listing
          </Button>
        </div>

        {loading ? (
          <div className="text-center text-muted-foreground py-10"><Loader2 className="animate-spin h-6 w-6 mx-auto" /></div>
        ) : listings.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="p-10 text-center space-y-3">
              <Store className="h-10 w-10 text-indigo-400 mx-auto" />
              <h3 className="text-lg font-semibold">No listings yet</h3>
              <p className="text-sm text-muted-foreground">Create your first listing and start getting hired across Denver.</p>
              <Button onClick={() => setEditing({})} className="bg-indigo-500 hover:bg-indigo-600">
                <Plus className="h-4 w-4 mr-1" /> Create listing
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {listings.map((ls) => (
              <Card key={ls.listing_id} data-testid={`listing-${ls.listing_id}`} className="hover:border-indigo-500/40">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                    {ls.image_url
                      ? <img src={ls.image_url} alt="" className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center text-muted-foreground"><Store className="h-6 w-6" /></div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold truncate">{ls.business_name}</h3>
                      <Badge className="text-[10px] bg-indigo-500/20 text-indigo-300 border-indigo-500/30">{ls.sub_category}</Badge>
                      <Badge className={`text-[10px] ${ls.is_active ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' : 'bg-muted'}`}>
                        {ls.is_active ? 'Active' : 'Paused'}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                      {ls.city} · {ls.pricing_model === 'hourly' ? `$${ls.base_price}/hr` : ls.pricing_model === 'flat_rate' ? `$${ls.base_price} flat` : ls.pricing_model}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={ls.is_active} onCheckedChange={() => toggleActive(ls)} title="Active" />
                    <Button variant="ghost" size="sm" onClick={() => setEditing(ls)} data-testid={`edit-${ls.listing_id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(ls.listing_id)} data-testid={`delete-${ls.listing_id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {editing && (
          <ListingDialog
            listing={editing}
            token={token}
            onClose={(updated) => { setEditing(null); if (updated) refresh(); }}
          />
        )}
      </div>
    </div>
  );
}

function ListingDialog({ listing, token, onClose }) {
  const isNew = !listing.listing_id;
  const [form, setForm] = useState({
    business_name: listing.business_name || '',
    sub_category: listing.sub_category || 'event-staffing',
    description: listing.description || '',
    pricing_model: listing.pricing_model || 'hourly',
    base_price: listing.base_price ?? '',
    min_hours: listing.min_hours ?? 0,
    min_staff: listing.min_staff ?? 1,
    image_url: listing.image_url || '',
    city: listing.city || 'denver',
    skills: (listing.skills || []).join(', '),
    contact_email: listing.contact_email || '',
    contact_phone: listing.contact_phone || '',
    website_url: listing.website_url || '',
  });
  const [saving, setSaving] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const submit = async () => {
    if (!form.business_name || !form.sub_category) {
      toast.error('Business name and category are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        base_price: parseFloat(form.base_price) || 0,
        min_hours: parseInt(form.min_hours) || 0,
        min_staff: parseInt(form.min_staff) || 1,
        skills: form.skills.split(',').map((s) => s.trim()).filter(Boolean),
      };
      if (isNew) {
        await axios.post(`${API}/staff-services`, payload, auth);
        toast.success('Listing published!');
      } else {
        await axios.patch(`${API}/staff-services/${listing.listing_id}`, payload, auth);
        toast.success('Listing updated');
      }
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={() => onClose(false)}>
      <DialogContent className="bg-card max-w-2xl max-h-[95vh] overflow-y-auto" data-testid="listing-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? 'New Staffing Service Listing' : `Edit · ${listing.business_name}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <F label="Business Name *"><Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder="Destinie's Event Staffing" data-testid="listing-business-name" /></F>
            <F label="Category *">
              <Select value={form.sub_category} onValueChange={(v) => setForm({ ...form, sub_category: v })}>
                <SelectTrigger data-testid="listing-sub-category"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {SUB_CATEGORIES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
          </div>
          <F label="Description">
            <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What you offer, how you work, what makes you different…" />
          </F>
          <F label="Cover image URL"><Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://…" /></F>
          <div className="grid grid-cols-3 gap-3">
            <F label="Pricing Model">
              <Select value={form.pricing_model} onValueChange={(v) => setForm({ ...form, pricing_model: v })}>
                <SelectTrigger data-testid="listing-pricing-model"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRICING_MODELS.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Base Price $"><Input type="number" min="0" step="1" value={form.base_price} onChange={(e) => setForm({ ...form, base_price: e.target.value })} /></F>
            <F label="Min hours">
              <Input type="number" min="0" step="1" value={form.min_hours} onChange={(e) => setForm({ ...form, min_hours: e.target.value })} />
            </F>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Min staff"><Input type="number" min="1" value={form.min_staff} onChange={(e) => setForm({ ...form, min_staff: e.target.value })} /></F>
            <F label="City"><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="denver" /></F>
          </div>
          <F label="Skills / specialties (comma-separated)">
            <Input value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} placeholder="TIPS certified, Black-tie, Bilingual" />
          </F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Contact email"><Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></F>
            <F label="Contact phone"><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="(555) 555-5555" /></F>
          </div>
          <F label="Website (optional)"><Input value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} placeholder="https://" /></F>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onClose(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-indigo-500 hover:bg-indigo-600" data-testid="listing-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
            {isNew ? 'Publish listing' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

// ===== Optional: read-only public directory strip (for other pages) =====
export function StaffServicesDirectory({ city = 'denver', limit = 8 }) {
  const [listings, setListings] = useState([]);
  useEffect(() => {
    axios.get(`${API}/staff-services?city=${city}&limit=${limit}`)
      .then((r) => setListings(r.data.listings || []))
      .catch(() => {});
  }, [city, limit]);
  if (listings.length === 0) return null;
  return (
    <section className="space-y-3" data-testid="staff-services-directory">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold font-['Space_Grotesk'] flex items-center gap-2">
          <Store className="h-5 w-5 text-indigo-400" /> Hire Event Staff Services
        </h2>
        <Link to="/staffing/my-services" className="text-xs text-indigo-400 hover:underline">List your service →</Link>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {listings.map((ls) => (
          <Card key={ls.listing_id} className="overflow-hidden hover:border-indigo-500/40" data-testid={`public-listing-${ls.listing_id}`}>
            <div className="aspect-video bg-muted">
              {ls.image_url
                ? <img src={ls.image_url} alt="" className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center text-muted-foreground"><Store className="h-8 w-8" /></div>}
            </div>
            <CardContent className="p-3 space-y-1">
              <Badge className="text-[10px] bg-indigo-500/20 text-indigo-300 border-indigo-500/30 uppercase">{ls.sub_category}</Badge>
              <h3 className="font-semibold text-sm truncate">{ls.business_name}</h3>
              <p className="text-xs text-muted-foreground line-clamp-2">{ls.description}</p>
              <div className="flex items-center justify-between pt-1 text-xs">
                <span className="tabular-nums font-semibold">
                  {ls.pricing_model === 'hourly' ? `$${ls.base_price}/hr` : ls.pricing_model === 'flat_rate' ? `$${ls.base_price}` : 'Quote'}
                </span>
                <span className="text-muted-foreground flex items-center gap-0.5"><MapPin className="h-3 w-3" />{ls.city}</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}
